import React, { useMemo, useState, useEffect } from "react";
import { Card, Divider, Tag, Typography, Tooltip, Badge, Row, Col } from "antd";
import { 
  EuroCircleOutlined, 
  TagOutlined, 
  BarcodeOutlined,
  StockOutlined,
  TruckOutlined,
  BoxPlotOutlined,
  ArrowsAltOutlined,
  FileTextOutlined,
  ShoppingOutlined,
  ExperimentOutlined
} from "@ant-design/icons";

const { Title, Text } = Typography;

const fileToUrl = (f) =>
  f?.url || f?.thumbUrl || (f?.originFileObj ? URL.createObjectURL(f.originFileObj) : "");

export default function ProductLivePreview({
  productName,
  descriptionHTML,
  images,
  categories = [],
  sizesSelected = [],
  colorsSelected = [],
  price,
  isSuperAdmin = false,
  costPrice,
  sku,
  duplicateCount,
  stock,
  shippingAvailable,
  packageCount,
  length,
  width,
  height,
  allSizes = [],
  sizePriceRows = {},
}) {
  const mainImg = images?.length ? fileToUrl(images[0]) : null;

  // Local state to handle real-time updates
  const [localProductName, setLocalProductName] = useState(productName || "");
  const [localDescription, setLocalDescription] = useState(descriptionHTML || "");
  const [localCategories, setLocalCategories] = useState(categories || []);
  const [localSizes, setLocalSizes] = useState(sizesSelected || []);
  const [localColors, setLocalColors] = useState(colorsSelected || []);
  const [localPrice, setLocalPrice] = useState(price || "");
  const [localCostPrice, setLocalCostPrice] = useState(costPrice || "");
  const [localSku, setLocalSku] = useState(sku || "");
  const [localStock, setLocalStock] = useState(stock || 0);
  const [localShipping, setLocalShipping] = useState(shippingAvailable || false);
  const [localPackageCount, setLocalPackageCount] = useState(packageCount || 1);
  const [localDimensions, setLocalDimensions] = useState({
    length: length || "",
    width: width || "",
    height: height || ""
  });

  // Update local state when props change
  useEffect(() => {
    setLocalProductName(productName || "");
  }, [productName]);

  useEffect(() => {
    setLocalDescription(descriptionHTML || "");
  }, [descriptionHTML]);

  useEffect(() => {
    setLocalCategories(categories || []);
  }, [categories]);

  useEffect(() => {
    setLocalSizes(sizesSelected || []);
  }, [sizesSelected]);

  useEffect(() => {
    setLocalColors(colorsSelected || []);
  }, [colorsSelected]);

  useEffect(() => {
    setLocalPrice(price || "");
  }, [price]);

  useEffect(() => {
    setLocalCostPrice(costPrice || "");
  }, [costPrice]);

  useEffect(() => {
    setLocalSku(sku || "");
  }, [sku]);

  useEffect(() => {
    setLocalStock(stock || 0);
  }, [stock]);

  useEffect(() => {
    setLocalShipping(shippingAvailable || false);
  }, [shippingAvailable]);

  useEffect(() => {
    setLocalPackageCount(packageCount || 1);
  }, [packageCount]);

  useEffect(() => {
    setLocalDimensions({
      length: length || "",
      width: width || "",
      height: height || ""
    });
  }, [length, width, height]);

  const selectedSizeObjs = useMemo(
    () => (allSizes || []).filter((s) => localSizes.includes(s.size)),
    [allSizes, localSizes]
  );

  // Calculate price range for display
  const priceRange = useMemo(() => {
    if (selectedSizeObjs.length === 0) return null;
    
    const prices = selectedSizeObjs
      .map(s => {
        const row = sizePriceRows[s.id] || {};
        return typeof row.price === 'number' ? row.price : null;
      })
      .filter(price => price !== null);
    
    if (prices.length === 0) return null;
    
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    
    return {
      min,
      max,
      isRange: min !== max,
      count: prices.length
    };
  }, [selectedSizeObjs, sizePriceRows]);

  // Enhanced image gallery
  const renderImageGallery = () => (
    <div className="space-y-3">
      {/* Main Image */}
      <div
        className="rounded-xl overflow-hidden relative group"
        style={{
          height: 220,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          border: "1px solid #eef2ff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {mainImg ? (
          <>
            <img
              src={mainImg}
              alt="preview-main"
              className="transition-transform duration-300 group-hover:scale-105"
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all duration-300" />
          </>
        ) : (
          <div className="text-white flex flex-col items-center">
            <div className="text-2xl mb-2">📷</div>
            <div className="text-sm">No image selected</div>
          </div>
        )}
      </div>

      {/* Thumbnails */}
      {images?.length > 1 && (
        <div className="flex gap-2 flex-wrap items-center">
          {images.slice(0, 6).map((f, i) => (
            <div
              key={f.uid || i}
              className="relative group cursor-pointer transition-all duration-200 hover:scale-110 hover:shadow-lg"
              style={{
                width: 50,
                height: 50,
                borderRadius: 10,
                overflow: "hidden",
                border: "2px solid #e5e7eb",
                background: "#fff",
              }}
            >
              <img
                src={fileToUrl(f)}
                alt={`thumb-${i}`}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200" />
            </div>
          ))}
          {images.length > 6 && (
            <Badge 
              count={`+${images.length - 6}`} 
              className="site-badge-count-4" 
              style={{ 
                backgroundColor: '#667eea',
                fontWeight: '600'
              }}
            />
          )}
        </div>
      )}
    </div>
  );

  // Enhanced pricing section
  const renderPricing = () => (
    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-100 shadow-sm">
      <div className="flex items-center gap-2 mb-3">
        <EuroCircleOutlined className="text-blue-600 text-lg" />
        <div className="text-sm font-semibold text-gray-800">Pricing Information</div>
      </div>
      
      <div className="space-y-3">
        {/* Base Price */}
        {localPrice && (
          <div className="flex justify-between items-center">
            <div className="text-xs text-gray-600">Base Price</div>
            <Text className="text-sm font-semibold text-gray-800">€{localPrice}</Text>
          </div>
        )}

        {/* Per-Size Pricing */}
        {priceRange && (
          <div className="flex justify-between items-center">
            <div className="text-xs text-gray-600">Size Range</div>
            <div className="text-right">
              <Text className="text-lg font-bold text-emerald-600">
                {priceRange.isRange ? `€${priceRange.min} – €${priceRange.max}` : `€${priceRange.min}`}
              </Text>
              <div className="text-xs text-gray-500 mt-1">
                {priceRange.count} size{priceRange.count !== 1 ? 's' : ''}
              </div>
            </div>
          </div>
        )}

        {/* Cost Price for Super Admin */}
        {isSuperAdmin && localCostPrice && (
          <div className="flex justify-between items-center pt-2 border-t border-blue-100">
            <div className="text-xs text-gray-600">Cost Price</div>
            <Text className="text-sm font-semibold text-gray-700">€{localCostPrice}</Text>
          </div>
        )}
      </div>
    </div>
  );

  // Enhanced info cards
  const renderInfoCards = () => (
    <Row gutter={[8, 8]} className="mb-4">
      <Col span={12}>
        <div className="bg-white rounded-lg border border-gray-200 p-3 text-center shadow-sm hover:shadow-md transition-shadow duration-200">
          <StockOutlined className="text-blue-500 text-lg mb-1" />
          <div className="text-xs text-gray-500 mb-1">Stock</div>
          <div className="text-sm font-bold text-gray-800">{Number.isFinite(localStock) ? localStock : 0}</div>
        </div>
      </Col>
      <Col span={12}>
        <div className="bg-white rounded-lg border border-gray-200 p-3 text-center shadow-sm hover:shadow-md transition-shadow duration-200">
          <TruckOutlined className={localShipping ? "text-green-500" : "text-gray-400"} />
          <div className="text-xs text-gray-500 mb-1">Shipping</div>
          <div className={`text-xs font-semibold ${localShipping ? 'text-green-600' : 'text-gray-500'}`}>
            {localShipping ? "Available" : "Not Available"}
          </div>
        </div>
      </Col>
      <Col span={12}>
        <div className="bg-white rounded-lg border border-gray-200 p-3 text-center shadow-sm hover:shadow-md transition-shadow duration-200">
          <BoxPlotOutlined className="text-purple-500" />
          <div className="text-xs text-gray-500 mb-1">Packages</div>
          <div className="text-sm font-bold text-gray-800">{Number.isFinite(localPackageCount) ? localPackageCount : 1}</div>
        </div>
      </Col>
      <Col span={12}>
        <div className="bg-white rounded-lg border border-gray-200 p-3 text-center shadow-sm hover:shadow-md transition-shadow duration-200">
          <BarcodeOutlined className="text-orange-500" />
          <div className="text-xs text-gray-500 mb-1">Copies</div>
          <div className="text-sm font-bold text-gray-800">{duplicateCount || 1}</div>
        </div>
      </Col>
    </Row>
  );

  // Enhanced categories
  const renderCategories = () => (
    <div className="mb-4">
      <div className="flex items-center gap-2 mb-2">
        <ShoppingOutlined className="text-gray-500" />
        <div className="text-sm font-semibold text-gray-700">Categories</div>
      </div>
      <div className="flex flex-wrap gap-2">
        {localCategories.map((c) => (
          <Tag 
            key={c} 
            color="blue"
            className="px-3 py-1 rounded-full font-medium border-0 shadow-sm hover:shadow-md transition-all duration-200"
          >
            {c}
          </Tag>
        ))}
        {localCategories.length === 0 && (
          <div className="text-xs text-gray-400 italic">No categories selected</div>
        )}
      </div>
    </div>
  );

  // Enhanced sizes section
  const renderSizes = () => (
    <div className="mb-4">
      <div className="flex items-center gap-2 mb-2">
        <ArrowsAltOutlined className="text-gray-500" />
        <div className="text-sm font-semibold text-gray-700">Available Sizes</div>
      </div>
      <div className="flex flex-wrap gap-2">
        {localSizes.map((s) => (
          <Tag 
            key={s} 
            color="green"
            className="px-3 py-1 rounded-full font-medium border-0 shadow-sm"
          >
            {s}
          </Tag>
        ))}
        {localSizes.length === 0 && (
          <div className="text-xs text-gray-400 italic">No sizes selected</div>
        )}
      </div>
    </div>
  );

  // Enhanced colors section
  const renderColors = () => (
    <div className="mb-4">
      <div className="flex items-center gap-2 mb-2">
        <ExperimentOutlined className="text-gray-500" />
        <div className="text-sm font-semibold text-gray-700">Colors</div>
      </div>
      <div className="flex flex-wrap gap-2">
        {localColors.map((c) => (
          <Tag 
            key={c} 
            color="purple"
            className="px-3 py-1 rounded-full font-medium border-0 shadow-sm"
          >
            {c}
          </Tag>
        ))}
        {localColors.length === 0 && (
          <div className="text-xs text-gray-400 italic">No colors selected</div>
        )}
      </div>
    </div>
  );

  // Enhanced dimensions
  const renderDimensions = () => (
    <div className="mb-4">
      <div className="flex items-center gap-2 mb-2">
        <ArrowsAltOutlined className="text-gray-500" />
        <div className="text-sm font-semibold text-gray-700">Dimensions (cm)</div>
      </div>
      <Row gutter={8}>
        <Col span={8}>
          <div className="bg-white rounded-lg border border-gray-200 p-2 text-center shadow-sm">
            <div className="text-xs text-gray-500 mb-1">Length</div>
            <div className="text-sm font-semibold text-gray-800">{localDimensions.length || "—"}</div>
          </div>
        </Col>
        <Col span={8}>
          <div className="bg-white rounded-lg border border-gray-200 p-2 text-center shadow-sm">
            <div className="text-xs text-gray-500 mb-1">Width</div>
            <div className="text-sm font-semibold text-gray-800">{localDimensions.width || "—"}</div>
          </div>
        </Col>
        <Col span={8}>
          <div className="bg-white rounded-lg border border-gray-200 p-2 text-center shadow-sm">
            <div className="text-xs text-gray-500 mb-1">Height</div>
            <div className="text-sm font-semibold text-gray-800">{localDimensions.height || "—"}</div>
          </div>
        </Col>
      </Row>
    </div>
  );

  // Enhanced per-size prices table
  const renderSizePrices = () => (
    <div className="mb-4">
      <div className="flex items-center gap-2 mb-2">
        <TagOutlined className="text-gray-500" />
        <div className="text-sm font-semibold text-gray-700">Per-Size Pricing</div>
      </div>
      <div className="rounded-lg border border-gray-200 overflow-hidden shadow-sm">
        <div className={`grid ${isSuperAdmin ? "grid-cols-3" : "grid-cols-2"} gap-4 text-xs font-semibold text-gray-600 px-4 py-3 bg-gradient-to-r from-gray-50 to-gray-100`}>
          <div className="flex items-center gap-1">
            <ArrowsAltOutlined />
            <span>Variant</span>
          </div>
          {isSuperAdmin && <div className="text-center">Cost (€)</div>}
          <div className="text-center">Sale (€)</div>
        </div>
        {selectedSizeObjs.map((s, idx) => {
          const row = sizePriceRows[s.id] || {};
          return (
            <div
              key={s.id || idx}
              className={`grid ${isSuperAdmin ? "grid-cols-3" : "grid-cols-2"} gap-4 text-sm px-4 py-3 border-t border-gray-100 ${
                idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'
              } hover:bg-blue-50 transition-colors duration-150`}
            >
              <div className="font-semibold text-gray-800 flex items-center">
                <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                {s.size}
              </div>
              {isSuperAdmin && (
                <div className="text-center font-medium text-gray-700">
                  {row?.cost !== "" && row.cost != null ? `€${row.cost}` : "—"}
                </div>
              )}
              <div className="text-center font-bold text-emerald-600">
                {row?.price !== "" && row.price != null ? `€${row.price}` : "—"}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  // Enhanced description
  const renderDescription = () => (
    <div className="mb-4">
      <div className="flex items-center gap-2 mb-2">
        <FileTextOutlined className="text-gray-500" />
        <div className="text-sm font-semibold text-gray-700">Description</div>
      </div>
      <div 
        className="prose prose-sm max-w-none text-gray-700 bg-white rounded-lg border border-gray-200 p-4 shadow-sm"
        dangerouslySetInnerHTML={{ __html: localDescription }}
      />
    </div>
  );

  return (
    <Card
      className="shadow-2xl rounded-2xl border-0"
      bodyStyle={{ padding: 0 }}
      title={
        <div className="flex items-center gap-3 px-2">
          <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
          <span className="font-bold text-gray-800 text-lg">Live Preview</span>
          <div className="flex-1"></div>
          <Badge count="Live" style={{ backgroundColor: '#10b981' }} />
        </div>
      }
      headStyle={{ 
        borderBottom: "1px solid #e5e7eb",
        padding: "16px 20px",
        background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)"
      }}
    >
      <div className="p-6 space-y-6">
        {/* Image Gallery */}
        {renderImageGallery()}

        {/* Title */}
        <div>
          <Title level={3} className="!mt-0 !mb-3 !text-gray-800 border-b pb-2">
            {localProductName?.trim() || "Untitled Product"}
          </Title>
        </div>

        {/* Categories */}
        {!!localCategories?.length && renderCategories()}

        {/* Pricing */}
        {(localPrice || priceRange || (isSuperAdmin && localCostPrice)) && renderPricing()}

        {/* Info Cards */}
        {renderInfoCards()}

        {/* Sizes */}
        {!!localSizes?.length && renderSizes()}

        {/* Colors */}
        {!!localColors?.length && renderColors()}

        {/* Dimensions */}
        {(localDimensions.length || localDimensions.width || localDimensions.height) && renderDimensions()}

        {/* Per-Size Prices */}
        {!!selectedSizeObjs.length && renderSizePrices()}

        {/* Description */}
        {localDescription?.trim() && renderDescription()}

        {/* Empty State */}
        {!localProductName && !localDescription && images.length === 0 && localCategories.length === 0 && (
          <div className="text-center py-8">
            <div className="text-gray-500 text-sm">
              Start filling the form to see live preview updates
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}